import BlockCreateEmailModal from '@/components/BlockCreateEmailModal';
import ExploreTemplateModal from '@/components/ExploreTemplateModal';
import Loading from '@/components/Loading';
import { PopupCreateProduct } from '@/components/Products';
import { EMPTY_PAGE_CONTENT, SPACE_SUBSCRIPTIONS } from '@/constants';
import { useSpaceIDFromPath } from '@/data/space/hooks';
import { CollectionSetupFlowOpenDispatchContext } from '@/sharedComponents/CollectionSetupFlow/context/openContext';
import { initialValueEmpty } from '@/sharedComponents/TextEditor';
import {
  useCreateEmailMessageMutation,
  useCreateFormMutation,
  useCreatePageMutation,
  useFeatureFlag,
  useGetSpaceQuery,
  useSaveContentMutation,
  useUpdateFormContentMutation,
  useUpdatePageMetadataMutation,
} from '@/store';
import flags from '@/store/flags';
import { generateLinkInBioTemplate } from '@/utils/generateLinkInBioTemplate';
import TokenContext from '@/utils/jwt/tokenContext';
import { AddCircle } from '@styled-icons/material-outlined';
import { DreamValue } from '@typedream/core';
import {
  SPACE_ACTION,
  SUBSCRIPTION_PLAN,
  TEMPLATE_TYPE,
  errToString,
  validUUID,
} from '@typedream/data';
import { Button } from '@typedream/ui';
import { useRouter } from 'next/router';
import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { MenuAddWorkspaceProps } from './types';

export const MenuAddWorkspace = memo(({ menu }: MenuAddWorkspaceProps) => {
  const router = useRouter();
  const spaceID = useSpaceIDFromPath();
  const [isLoading, setIsLoading] = useState(false);
  const [newProductOpen, setNewProductOpen] = useState(false);
  const [isShowTemplateModal, setIsShowTemplateModal] = useState(false);
  const [isShowBlogTemplateModal, setIsShowBlogTemplateModal] = useState(false);
  const [isShowBlockCreateEmail, setIsShowBlockCreateEmail] = useState(false);
  const [plan, setPlan] = useState(SUBSCRIPTION_PLAN.FREE);
  const { data: space } = useGetSpaceQuery([spaceID]);
  const { mutate: createPage } = useCreatePageMutation({ fixedCacheKey: `createPage-${spaceID}` });
  const { mutate: saveContent } = useSaveContentMutation();
  const { mutate: createForm } = useCreateFormMutation();
  const { mutate: updateFormContent } = useUpdateFormContentMutation();
  const { mutate: updatePageMetadata } = useUpdatePageMetadataMutation();
  const { mutate: createEmailMessage } = useCreateEmailMessageMutation();

  const freeOrOldLaunch = [SUBSCRIPTION_PLAN.FREE, SUBSCRIPTION_PLAN.LAUNCH].includes(plan);

  const handleShowFormTemplate = useCallback(() => {
    setIsShowTemplateModal(true);
  }, []);
  const handleBlogTemplate = useCallback(() => {
    setIsShowBlogTemplateModal(true);
  }, []);
  const handleShowBlokCrateEmail = useCallback(() => {
    setIsShowBlockCreateEmail(true);
  }, []);
  /**
   * This component might be used in the `dashboard` or the `editor` page.
   * When this component used in the `editor` page, clicking the add blog
   * button should open the setup collection flow.
   */
  const setCollectionSetupFlowOpen = useContext(CollectionSetupFlowOpenDispatchContext);

  // Create new form
  const handleCreateNewForm = async (templateContent?: DreamValue) => {
    // create new form instance in the database
    const { data: form } = await createForm(spaceID as string);

    if (!form) {
      throw Error('Error creating new form instance');
    }

    // TODO(@ibedwi): do we need this tracker?
    // mixpanel.track(EVENT.CREATE_FORM, {
    //   spaceID: spaceID as string,
    // });

    await updateFormContent({
      spaceID: spaceID as string,
      formID: form.id as string,
      data: templateContent ?? [],
    });

    router.push(`/settings/${space?.slug}/${spaceID as string}/forms/${form.id}`);
  };

  const handleCreateNewPage = async (pageType?: 'page' | 'link_in_bio', content?: DreamValue) => {
    setIsLoading(true);

    try {
      const routes = space?.routes;

      // create a new page
      const { page: newPage } = await createPage({
        spaceID: spaceID as string,
        pagePath: [],
        routeAtPath: routes,
      }).unwrap();

      // redirect to the new page
      if (validUUID(newPage?.id)) {
        if (content) {
          await saveContent({
            spaceID: spaceID as string,
            pageID: newPage.id,
            newContent: content,
            force: true,
          });
        }

        switch (pageType) {
          case 'link_in_bio': {
            await updatePageMetadata({
              spaceID: spaceID as string,
              pageID: newPage.id,
              metadata: {
                ...newPage.metadata,
                page_type: pageType,
              },
              newTwitterImage: null,
              newFacebookImage: null,
            });

            setIsLoading(false);
            router.push(`/link/s/${spaceID}/${newPage?.id}`);
            break;
          }
          default: {
            const spaceSlug = space?.slug;
            setIsLoading(false);
            router.push(`/s/${spaceSlug}/${spaceID}/${newPage?.id}`);
            break;
          }
        }
      }
    } catch (err) {
      setIsLoading(false);
      toast.error(errToString(err));
    }
  };

  const { subscriptionTokenHandler } = useContext(TokenContext);
  const [upgradeGrowRequired, setUpgradeGrowRequired] = useState(false);
  const checkSubscription = useCallback(async () => {
    try {
      const sub = await subscriptionTokenHandler.getSubscription(spaceID as string);
      setPlan(sub?.getPlan(SPACE_SUBSCRIPTIONS));
      setUpgradeGrowRequired(
        !!space && !sub.isAllowed(SPACE_ACTION.SendEmail, SPACE_SUBSCRIPTIONS)
      );
    } catch (e) {
      setPlan(SUBSCRIPTION_PLAN.FREE);
    }
  }, [spaceID, subscriptionTokenHandler, space]);

  useEffect(() => {
    if (spaceID) {
      checkSubscription();
    }
  }, [checkSubscription, spaceID]);

  const pricingFlag = useFeatureFlag(flags.PricingLaunchGrow) && plan !== SUBSCRIPTION_PLAN.PRO;

  const handleCreateNewEmail = async (emailSubject = 'New Email') => {
    if (freeOrOldLaunch) {
      handleShowBlokCrateEmail();
      return;
    }

    setIsLoading(true);
    try {
      const emailMessage = await createEmailMessage(
        {
          subject: emailSubject,
          html: '',
          content: JSON.stringify(EMPTY_PAGE_CONTENT),
        },
        spaceID
      ).unwrap();
      router.push(`/email/${spaceID}/${emailMessage.id}`);
    } catch (err) {
      setIsLoading(false);
      toast.error(errToString(err));
    }
  };

  const handleClick = async () => {
    switch (menu) {
      case 'pages': {
        await handleCreateNewPage('page', initialValueEmpty);
        break;
      }
      case 'forms': {
        handleShowFormTemplate();
        break;
      }
      case 'blogs': {
        handleBlogTemplate();
        break;
      }
      case 'link_in_bio': {
        await handleCreateNewPage('link_in_bio', generateLinkInBioTemplate({}));
        break;
      }
      case 'products': {
        setNewProductOpen(true);
        break;
      }
      case 'emails': {
        await handleCreateNewEmail();
        break;
      }
      default:
        break;
    }
  };

  return (
    <>
      {isLoading && <Loading loading />}
      <Button
        onClick={() => handleClick()}
        variant="outline"
        className="p-1 bg-transparent text-tertiary rounded-full border-0 hover:shadow-none hover:bg-neutral-200 transition-all duration-200 ease-out"
      >
        <AddCircle size={16} />
      </Button>
      <PopupCreateProduct open={newProductOpen} setopen={setNewProductOpen} />
      <ExploreTemplateModal
        setIsShow={setIsShowTemplateModal}
        isShow={isShowTemplateModal}
        onUseTemplate={handleCreateNewForm}
        templateTypes={[TEMPLATE_TYPE.FORM]}
        showBlankOption
        onUseBlankTemplate={() => handleCreateNewForm()}
      />
      <ExploreTemplateModal
        setIsShow={setIsShowBlogTemplateModal}
        isShow={isShowBlogTemplateModal}
        onUseTemplate={(templateContent: DreamValue) => {
          handleCreateNewPage('page', templateContent);
        }}
        templateTypes={[TEMPLATE_TYPE.BLOG_PAGE]}
        showBlankOption
        onUseBlankTemplate={async () => {
          setCollectionSetupFlowOpen(true);
        }}
      />
      <BlockCreateEmailModal
        setIsShow={setIsShowBlockCreateEmail}
        isShow={isShowBlockCreateEmail}
      />
    </>
  );
});
